import React from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { Button } from 'ui-components/components/button';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Text, TextType } from 'ui-components/components/text';
import { noop } from 'ui-components/default-props/noop';

import '../../../../prop-types/add-on.types';
import { DeactivateAddOnButton } from './deactivate-add-on-button';
import { messages } from './messages';
import style from './style.module.css';
import { ModalPortal } from '../../../../components/modal-portal';
import { getUpsellModalContent } from '../../../../components/upsell-modal/helper';
import { AddOns } from '../../../../constants/add-ons';

// At some point the backend can return data regarding deactivation timing requirements per addon
const addOnsThatDeactivateImmediately = [AddOns.DropShip, AddOns.FulfillmentProviderBundle];
const deactivationContent = {
  [AddOns.DropShip]: () => <Text textType={TextType.Paragraph} value={messages.dropshipManagerDeactivationMessage} />,
  [AddOns.FulfillmentProviderBundle]: () => (
    <Text
      formattedMessageValues={{
        link: (chunks) => <Link to='/settings/stores'>{chunks}</Link>,
      }}
      textType={TextType.Paragraph}
      value={messages.fulfillmentProviderBundleDeactivationMessage}
    />
  ),
};

/**
 * @typedef {import('../../../../prop-types/add-on.types').AddOnPropType} AddOn
 */

/**
 * @param {object} props
 * @param {AddOn} props.addOn
 * @param {() => void} props.onClose
 * @param {(code: string) => void} props.onError
 * @returns {JSX.Element}
 */
export const AddOnDeactivationModal = ({ onClose, addOn, onError = noop }) => {
  const intl = useIntl();
  const content = getUpsellModalContent(addOn.code);
  const addOnName = intl.formatMessage(content.name);
  const willDeactivateImmediately = addOnsThatDeactivateImmediately.includes(addOn.code);
  const CustomContent = deactivationContent[addOn.code];

  return (
    <ModalPortal onClose={onClose}>
      <Modal className={style['deactivation-modal-text']}>
        <ModalHeader className={style['deactivation-modal-header']}>
          <Text formattedMessageValues={{ addOn: addOnName }} textType={TextType.Description} value={messages.header} />
        </ModalHeader>
        <ModalBody className={style['deactivate-modal-body']}>
          <div className={style['deactivate-modal-content']}>
            <Text
              className={style.confirmation}
              formattedMessageValues={{ addOn: addOnName }}
              textType={TextType.H3}
              value={messages.confirmation}
            />
            {CustomContent ? (
              <>
                <CustomContent />
                <br />
              </>
            ) : null}
            {willDeactivateImmediately ? (
              <Text
                className={style['deactivation-body-text']}
                formattedMessageValues={{ addOn: addOnName }}
                textType={TextType.Paragraph}
                value={messages.deactivateImmediate}
              />
            ) : null}
            {!willDeactivateImmediately && (
              <Text
                className={style['deactivation-body-text']}
                formattedMessageValues={{ addOn: addOnName }}
                textType={TextType.Paragraph}
                value={messages.endOfBillingCycle}
              />
            )}
          </div>
          <div className={style.divider} />
          <div className={style['deactivate-modal-buttons']}>
            <Button className={style['cancel-button']} onClick={onClose} theme='tertiary'>
              <Text textType={TextType.Paragraph} value={messages.cancel} />
            </Button>
            <DeactivateAddOnButton addOn={addOn} onCloseModal={onClose} onError={onError} />
          </div>
        </ModalBody>
      </Modal>
    </ModalPortal>
  );
};
